import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

const STORAGE_KEY = 'tucuate.device_id';

/** Tope que Chrome aplica a `Max-Age` desde la v104. */
const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Identificador de la instalación, enviado en `X-Device-Id`. Se guarda en `localStorage` y en una
 * cookie: no sobrevive a "borrar datos de navegación" (limpia ambos), pero sí a que uno de los dos
 * esté bloqueado o se limpie por separado.
 *
 * Todo acceso va protegido: leer `localStorage` o `document.cookie` lanza `SecurityError` con el
 * almacenamiento bloqueado, y esto corre en el arranque de la app.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdService {
  private mDeviceId: string | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  public getOrCreate(): string | null {
    if (this.mDeviceId) return this.mDeviceId;
    // En SSR no hay navegador detrás: no se genera ni se persiste nada.
    if (!isPlatformBrowser(this.platformId)) return null;

    this.mDeviceId = this.readStored() ?? this.generateUuidV4();
    // Siempre en los dos lados: si el id vino de la cookie, repuebla `localStorage`.
    this.writeLocalStorage(this.mDeviceId);
    this.writeCookie(this.mDeviceId);
    return this.mDeviceId;
  }

  /** Un valor corrupto lo rechaza el backend al castear a `uuid`, dejando al dispositivo sin registrar. */
  private readStored(): string | null {
    const stored = this.readLocalStorage() ?? this.readCookie();
    return stored && UUID_PATTERN.test(stored) ? stored : null;
  }

  private readLocalStorage(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private writeLocalStorage(value: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Bloqueado o lleno: queda la cookie.
    }
  }

  private readCookie(): string | null {
    try {
      // Sin regex: la clave tiene un punto que habría que escapar.
      const entry = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${STORAGE_KEY}=`));
      return entry ? decodeURIComponent(entry.slice(STORAGE_KEY.length + 1)) : null;
    } catch {
      return null;
    }
  }

  private writeCookie(value: string): void {
    try {
      // `Secure` solo bajo https: en http la haría inescribible y el dev server es http.
      const secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${STORAGE_KEY}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
    } catch {
      // Bloqueadas: queda `localStorage`.
    }
  }

  private generateUuidV4(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    // Safari < 15.4 no tiene `crypto.randomUUID`: se arma el v4 a mano desde `getRandomValues`,
    // que sí tiene soporte universal, en lugar de `Math.random()` (predecible).
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }
}
